import React, { ChangeEvent, useEffect, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Alert, Button, Grow, Snackbar, TextField } from '@mui/material';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import DealService from 'services/DealService';
import { useHistory, useParams } from 'react-router-dom';
import loginActions from '../../redux/logins/actions';
import logo from '../../assets/logo.png';
import { JsonDialog } from './JsonDialog';
import { CreditApplications } from './CreditApplications';
import { MergeLeadsOperations } from './MergeLeadsOperations';
import './LendingSupport.scss';
import { LendingSupportDealer } from './Dealer';
import { LendingSupportCar } from './Car';
import { LendingSupportDeal } from './Deal';
import { LendingSupportDataDogLinks } from './DataDogLinks';
import { LendingSupportCloudWatchLinks } from './CloudWatchLinks';
import { LendingSupportUpstartAdminLinks } from './UpstartAdminLinks';
import {
  GetLendingSupportDealFinanceOfferResponse,
  useLendingSupportService,
} from '../../hooks/services';
import { useCrmMergeLeadsOperations } from '../../hooks/api/crmMergeLeadsOperations';

interface Params {
  dealId: string;
}

/**
 * Main component that renders the Lending Support Tool, showing clickable
 * JSON data for a given deal.
 */
export const LendingSupport: React.FC = () => {
  const { dealId } = useParams<Params>();
  const history = useHistory();
  const { getDealFinanceOffer } = useLendingSupportService();

  const [dealIdInput, setDealIdInput] = useState<string>(dealId ?? '');
  const [deal, setDeal] = useState<Deal | undefined>();
  const [jsonData, setJsonData] = useState<object | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [dealFinanceOffers, setDealFinanceOffers] =
    useState<GetLendingSupportDealFinanceOfferResponse>();
  const { operations: crmMergeLeadsOperations } =
    useCrmMergeLeadsOperations(dealId);

  // Fetch applications
  useEffect(() => {
    if (!deal) {
      return;
    }
    getDealFinanceOffer({ dealId: deal.id })
      .then((data: GetLendingSupportDealFinanceOfferResponse) => {
        setDealFinanceOffers(data);
      })
      .catch(() => {
        setDealFinanceOffers(undefined);
        setAlertMessage('Unable to fetch deal finance offers');
      });
  }, [deal]);

  useEffect(() => {
    if (dealId) {
      loadHandler();
    }
  }, [dealId]);

  const clearLocalState = () => {
    setDeal(undefined);
    setDealFinanceOffers(undefined);
  };

  const loadHandler = () => {
    if (dealIdInput) {
      clearLocalState();
      DealService.getDeal(dealIdInput)
        .then((dealObj) => {
          setDeal(dealObj);
        })
        .catch(() => {
          // error fetching deal, so set everything to null
          clearLocalState();
          setAlertMessage('Unable to fetch deal');
        });
    }
  };

  const redirectDeal = () => {
    if (dealIdInput) {
      history.push(`/lending-support/${dealIdInput}`);
    }
  };

  const onEntityClick = (data?: object | null) => {
    if (data) {
      setJsonData(data);
      setIsDialogOpen(true);
    }
  };

  const renderDealId = () => (
    <div className="lending-support-dealid-container">
      <TextField
        id="dealId"
        data-testid="lending-support-dealid-textfield"
        label="Deal ID"
        value={dealIdInput}
        size="small"
        InputLabelProps={{
          shrink: true,
          sx: {
            fontSize: 12,
          },
        }}
        inputProps={{ style: { fontSize: 14 } }}
        sx={{ width: '38ch' }}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setDealIdInput(event.target.value);
        }}
      />
      <Button variant="contained" onClick={redirectDeal}>
        Load
      </Button>
    </div>
  );

  const renderDeal = () => (
    <>
      {deal && (
        <div className="lending-support-deal-container">
          <LendingSupportDataDogLinks deal={deal} />
          <LendingSupportCloudWatchLinks deal={deal} />
          <LendingSupportUpstartAdminLinks
            dealFinanceOffers={dealFinanceOffers}
          />
        </div>
      )}
      <div className="lending-support-deal-container">
        <LendingSupportDeal deal={deal} onClick={onEntityClick} />
        <LendingSupportDealer
          dealerId={deal?.dealer_id}
          onClick={onEntityClick}
          onError={setAlertMessage}
        />
        <LendingSupportCar
          carId={deal?.car_id}
          onClick={onEntityClick}
          onError={setAlertMessage}
        />
      </div>
      {dealFinanceOffers && (
        <>
          <div className="lending-support-header">Finance Applications</div>
          <div className="lending-support-credit-apps-container">
            <CreditApplications
              creditAppList={dealFinanceOffers}
              onClick={onEntityClick}
            />
          </div>
        </>
      )}
      {deal && crmMergeLeadsOperations && (
        <>
          <div className="lending-support-header">
            CRM Merge Leads Operations
          </div>
          <div className="lending-support-merge-operations-container">
            <MergeLeadsOperations operations={crmMergeLeadsOperations} />
          </div>
        </>
      )}
      <JsonDialog
        open={isDialogOpen}
        data={jsonData ?? {}}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );

  return (
    <div
      className="lending-support-container"
      data-testid="lending-support-container"
    >
      <div className="lending-support-logo-container">
        <img src={logo} alt="Logo" className="logo" />
      </div>
      <div className="lending-support-title">Lending Support Tool</div>
      {renderDealId()}
      <Snackbar
        open={!!alertMessage}
        autoHideDuration={4000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        TransitionComponent={Grow}
        onClose={() => setAlertMessage('')}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          <span style={{ fontSize: '14px' }}>{alertMessage}</span>
        </Alert>
      </Snackbar>
      {dealId && renderDeal()}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  auth: state.auth,
  logins: state.logins,
  user: state.user,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators({ ...loginActions }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(LendingSupport);
